import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

import Heading from 'components/shared/heading';
import Button from 'components/shared/button';
import cn from 'classnames';
import PhoneIcon from 'components/icons/Phone';
import ArrowRightIcon from 'components/icons/ArrowRight';
import scrollToElement from 'utils/scroll-to-element';
import shape1 from './svg/shape-1.svg';
import shape2 from './svg/shape-2.svg';
import styles from './styles.module.scss';

const CallToAction = ({
  title = '',
  subTitle = '',
  description = '',
  buttonLabel = '',
  buttonLink = '',
  buttonIcon = '',
  secondaryLinks = [],
  secondaryLinksType = 'link',
  theme = '',
  customUniqId = null,
}) => {
  const handleHashClick = useCallback(
    (targetId) => (e) => {
      e.preventDefault();
      const targetElement = document.getElementById(targetId.replace('#', ''));
      scrollToElement(targetElement, { behavior: 'smooth' });
    },
    []
  );

  return (
    <div
      className={cn(styles.callToAction, {
        [styles.themeLight]: theme === 'light',
        [styles.themeDark]: theme === 'dark',
      })}
      id={customUniqId}
    >
      <div className={styles.hero}>
        {subTitle && (
          <Heading className={styles.subTitle} tag="p" size="md">
            {subTitle}
          </Heading>
        )}
        {title && (
          <Heading className={styles.title} tag="h2" size="xl">
            {title}
          </Heading>
        )}
        {description && <p className={styles.description}>{description}</p>}
        <div className={styles.button}>
          {buttonLabel && buttonLink && (
            <Button
              theme="accent-primary"
              to={buttonLink}
              className={buttonIcon === 'phone' ? styles.btnPhone : ''}
            >
              {buttonIcon === 'phone' && <PhoneIcon />}
              {buttonLabel}
              {buttonIcon === 'arrow' && <ArrowRightIcon />}
            </Button>
          )}
        </div>
        {secondaryLinks && secondaryLinks.length > 0 && (
          <div className={styles.secondaryLinksSeparator}>OR</div>
        )}
        {secondaryLinks && secondaryLinks.length > 0 && (
          <div className={styles.secondaryLinks}>
            {secondaryLinks.map((link, index) => {
              const isHashLink = link.link && link.link.startsWith('#');
              return (
                <React.Fragment key={link.label}>
                  <a
                    href={link.link}
                    className={cn(styles.link, {
                      [styles.asButton]: secondaryLinksType === 'button',
                    })}
                    onClick={isHashLink ? handleHashClick(link.link) : undefined}
                  >
                    {link.label}
                  </a>
                  {index < secondaryLinks.length - 1 && <span className={styles.separator}>•</span>}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
      {theme === 'dark' && (
        <>
          <img className={styles.shape1} src={shape1} alt="" loading="lazy" aria-hidden />
          <img className={styles.shape2} src={shape2} alt="" loading="lazy" aria-hidden />
        </>
      )}
    </div>
  );
};

CallToAction.propTypes = {
  title: PropTypes.string,
  subTitle: PropTypes.string,
  description: PropTypes.string,
  buttonLabel: PropTypes.string,
  buttonLink: PropTypes.string,
  buttonIcon: PropTypes.string,
  secondaryLinks: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      link: PropTypes.string,
    })
  ),
  secondaryLinksType: PropTypes.oneOf(['link', 'button']),
  theme: PropTypes.oneOf(['light', 'dark']),
  customUniqId: PropTypes.string,
};

export default CallToAction;
